import BaseAPI from './base.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';

export interface UploadGradeBlocksResult {
  message: string;
  rowsProcessed: number;
  rowsSkipped: number;
  gradesCreated: number;
  gradesUpdated: number;
  gradesFailed: number;
  errors: Array<{ row: number; message: string }>;
  failedBlocks: Array<Record<string, unknown>>;
}

export interface FailedGrade extends Record<string, unknown> {
  name: string;
  reason: string;
}

export interface BulkSaveGradesResult {
  created: number;
  failed: number;
  failedGrades: FailedGrade[];
  message: string;
}

export default class GradeImporterAPI extends BaseAPI {
  getGrades = async (): Promise<GradeRecord[]> => {
    const response = await this.API.get('grade-importer', {
      params: { customerId: this.customerId },
    });
    const data: Array<Record<string, unknown>> = Array.isArray(response.data)
      ? response.data
      : [];
    // API returns gradeId (not id) — normalise so GradeRecord.id is always set
    return data.map((r) => ({
      ...(r as unknown as GradeRecord),
      id: ((r.gradeId ?? r.id) as string) ?? '',
    }));
  };

  uploadGradeBlocks = async (file: File): Promise<UploadGradeBlocksResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.API.post(
      'grade-importer/upload-blocks',
      formData,
      {
        params: { customerId: this.customerId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  };

  downloadGradeBlocksTemplate = async (): Promise<Blob> => {
    const response = await this.API.get('grade-importer/template', {
      params: { customerId: this.customerId },
      responseType: 'blob',
    });
    return response.data;
  };

  updateGrade = async (
    id: string,
    payload: Omit<GradeRecord, 'id'>
  ): Promise<void> => {
    await this.API.put(`grade-importer/${id}`, payload);
  };

  bulkCreate = async (
    grades: Array<Omit<GradeRecord, 'id'>>
  ): Promise<BulkSaveGradesResult> => {
    const response = await this.API.post('grade-importer/bulk', {
      grades: grades.map((g) => ({ ...g, customerId: this.customerId })),
    });
    return response.data;
  };

  deleteGrade = async (id: string): Promise<void> => {
    await this.API.delete(`grade-importer/${id}`);
  };
}
